import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { AuthActionType } from './auth.type';
import { Subject, takeUntil } from 'rxjs';
import { AppSettings } from '@Shared/app.settings';
import { Router } from '@angular/router';
import { AuthService } from './services';
import { NotificationService, StorageService } from '@Shared/services';
import {
  PricingService,
  PricingStep
} from '@App/pages/pricing/pricing.service';
import { EmployerPortalService } from '@App/modules/choosen-history/services';
import { CardPackageService } from '@App/modules/cards-packages/services';
import { UserCommonApiService } from '@App/common/services';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import {
  ForgotPasswordComponent,
  ResetPasswordComponent,
  SignInComponent,
  SignUpComponent
} from '@App/pages/auth/components';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    SignInComponent,
    SignUpComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthComponent implements OnInit, OnDestroy {
  // signal-based state
  actionTypeSig: WritableSignal<AuthActionType | null> =
    signal<AuthActionType | null>(null);
  visibleSig = computed(() => !!this.actionTypeSig());
  // TODO: temporary solution for styles before refactor update
  styles: any = {
    SIGN_IN: {
      'max-width': '973px',
      width: '100%',
      padding: '3rem',
      margin: '5px',
      background: 'linear-gradient(180deg, #FEFEFE 0%, #E9F6F7 120.22%)'
    },
    IMPERSONATE_SIGN_IN: {
      'max-width': '544px',
      width: '100%',
      padding: '22px 30px',
      margin: '5px'
    },
    SIGN_UP: {
      'max-width': '973px',
      width: '100%',
      padding: '22px 30px',
      margin: '5px',
      background: 'linear-gradient(180deg, #FEFEFE 0%, #E9F6F7 120.22%)'
    },
    RECOVER: {
      'max-width': '544px',
      width: '100%',
      padding: '22px 30px',
      margin: '5px',
      background: 'linear-gradient(180deg, #FEFEFE 0%, #E9F6F7 120.22%)'
    },
    RESET_PASSWORD: {
      'max-width': '544px',
      width: '100%',
      padding: '22px 30px',
      margin: '5px',
      background: 'linear-gradient(180deg, #FEFEFE 0%, #E9F6F7 120.22%)'
    }
  };
  @ViewChild('signInRef') signInRef: SignInComponent;
  @ViewChild('impersonateSignInRef') impersonateSignInRef: SignInComponent;
  @ViewChild('signUpRef') signUpRef: SignUpComponent;
  @ViewChild('forgotPasswordRef') forgotPasswordRef: ForgotPasswordComponent;
  @ViewChild('resetPasswordRef') resetPasswordRef: ResetPasswordComponent;
  public isAuthorized: boolean = false;
  private authService = inject(AuthService);
  private userCommonService = inject(UserCommonApiService);
  private teamService = inject(EmployerPortalService);
  private cardPackagesService = inject(CardPackageService);
  private storageService = inject(StorageService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private pricingService = inject(PricingService);
  private isDestroyed$: Subject<boolean> = new Subject<boolean>();

  constructor() {
    this.isAuthorized = this.userCommonService.isAuthenticated();
  }

  get isSignInVisible(): boolean {
    return this.visibleSig() && this.actionTypeSig() === 'SIGN_IN';
  }

  get isImpersonateSignInVisible(): boolean {
    return this.visibleSig() && this.actionTypeSig() === 'IMPERSONATE_SIGN_IN';
  }

  get isSignUpVisible(): boolean {
    return this.visibleSig() && this.actionTypeSig() === 'SIGN_UP';
  }

  get isForgotPasswordVisible(): boolean {
    return this.visibleSig() && this.actionTypeSig() === 'RECOVER';
  }

  get isResetPasswordVisible(): boolean {
    return this.visibleSig() && this.actionTypeSig() === 'RESET_PASSWORD';
  }

  ngOnInit() {
    this.initAuthTypeSubscribe();
    this.checkTeamToken();
  }

  initAuthTypeSubscribe() {
    this.authService.actionType$
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((action: AuthActionType) => {
        console.log(action);
        this.actionTypeSig.set(action);
        try {
          this.cdr.detectChanges();
        } catch (e) {}
      });
  }

  changeAction(action: AuthActionType) {
    console.log(action);
    this.actionTypeSig.set(action);
  }

  closeAuth(type?: AuthActionType) {
    if (type && this.actionTypeSig() !== type) {
      return;
    }

    this.resetAllForms();

    const context = this.authService.getCurrentContext();
    if (context?.isFromPricing) {
      this.pricingService.goBackAfterAuthClose(PricingStep.PlanSelection);
    }

    this.authService.setAction(null);
    const currentPath = this.router.url.split('?')[0];
    const targetPath = currentPath === '/reset-password' ? '/' : currentPath;

    this.router.navigate([targetPath], {
      queryParams: {
        type: null,
        serviceUrl: null,
        token: null,
        emailId: null
      },
      queryParamsHandling: 'merge'
    });
    this.cdr.detectChanges();
  }

  onAuthDialogVisibleChange(visible: boolean) {
    if (!visible) {
      this.resetAllForms();
      this.closeAuth();
    }
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  private resetAllForms(): void {
    this.signInRef?.resetForm();
    this.impersonateSignInRef?.resetForm();
    this.signUpRef?.resetForm();
    this.forgotPasswordRef?.resetForm();
    this.resetPasswordRef?.resetForm();
  }

  private checkTeamToken() {
    this.authService.actionParams$
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((token) => {
        if (token?.teamToken && this.isAuthorized) {
          this.teamService.joinTeamByInvite(token).subscribe({
            next: ({ data }) => {
              if (data.businessId) {
                this.storageService.setSession(
                  AppSettings.DRAFT_BUSINESS_ID,
                  data.businessId
                );
                this.cardPackagesService.selectedType = data.businessType;
                this.router
                  .navigate(['create'], {
                    queryParams: { businessId: data.businessId }
                  })
                  .then();
                this.authService.setAction(null);
              }
            },
            error: ({ message }) => {
              this.notificationService.showNotification('WARNING', message);
              this.authService.setAction(null);
            }
          });
        }
      });
  }
}
